import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, BookOpen, FileText, Brain, MessageSquare,
  CheckCircle, Clock, AlertCircle, Calendar, User,
  Plus, Award, Target, X, Upload, Paperclip, Globe,
  Youtube, CalendarCheck, XCircle, Sparkles, ExternalLink
} from 'lucide-react';
import PublicacionCard from '../components/PublicacionCard';
import PublicacionForm from '../components/PublicacionForm';
import TaskTable from '../components/TaskTable';
import EvaluationTable from '../components/EvaluationTable';
import Modal from '../components/Modal';
import AlumnoFinalGradeDisplay from '../components/AlumnoFinalGradeDisplay';

const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([^\s&?"'<]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const AlumnoCatedraDetailPage = () => {
  const { catedraId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const STATIC_ASSET_BASE_URL = process.env.REACT_APP_API_URL?.endsWith('/api')
    ? process.env.REACT_APP_API_URL.slice(0, -4)
    : (process.env.REACT_APP_API_URL || '');

  const [loading, setLoading] = useState(true);
  const [catedra, setCatedra] = useState(null);
  const [docenteInfo, setDocenteInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('tablon');
  const [currentAlumnoId, setCurrentAlumnoId] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Estados para Tablón
  const [publicaciones, setPublicaciones] = useState([]);
  const [isPublicacionModalOpen, setIsPublicacionModalOpen] = useState(false);
  const [editingPublicacion, setEditingPublicacion] = useState(null);
  const [publicationLoading, setPublicationLoading] = useState(false);

  // Estados para Tareas y Drag & Drop
  const [tareas, setTareas] = useState([]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [taskToSubmit, setTaskToSubmit] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Modal de Detalle de Tarea (Consigna, Recursos del Docente, Archivos Entregados y Feedback)
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);

  // Estados para Evaluaciones y Calificación Final
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [finalGradeResults, setFinalGradeResults] = useState(null);
  const [porcentajeMinimoAprobacion, setPorcentajeMinimoAprobacion] = useState(null);
  const [calificacionFinalConfigId, setCalificacionFinalConfigId] = useState(null);

  // Nuevos estados para Plan de Estudios y Asistencia
  const [planesDeClase, setPlanesDeClase] = useState([]);
  const [asistenciaData, setAsistenciaData] = useState({
    resumen: {
      diasPresente: 0,
      diasAusente: 0,
      diasEspeciales: 0,
      diasClaseNormal: 0,
      clasesComputadas: 0,
      porcentajeAsistencia: 100
    },
    registros: []
  });

  // Stats
  const [stats, setStats] = useState({
    totalPublicaciones: 0,
    totalTareas: 0,
    tareasPendientes: 0,
    tareasCompletadas: 0,
    totalEvaluaciones: 0,
    evaluacionesPendientes: 0,
    evaluacionesCompletadas: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setCurrentAlumnoId(decoded.alumnoId);
        setUserRole(decoded.role);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }

    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }

    fetchCatedraData();
  }, [catedraId]);

  const handleGoToTaskTab = (cId, taskId) => {
    handleGoToTab('tareas', taskId);
  };

  // Handlers para Tareas y Drag & Drop
  const handleOpenSubmitModal = (task) => {
    setTaskToSubmit(task);
    setSelectedFiles([]);
    setIsDragging(false);
    setIsSubmitModalOpen(true);
  };

  const handleViewStudentTaskDetail = (task) => {
    setSelectedTaskForDetail(task);
    setIsTaskDetailModalOpen(true);
  };

  const addFilesToSelection = (newFilesArray) => {
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map(f => `${f.name}-${f.size}`));
      const uniqueNew = newFilesArray.filter(f => !existingNames.has(`${f.name}-${f.size}`));
      return [...prev, ...uniqueNew];
    });
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    addFilesToSelection(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      addFilesToSelection(droppedFiles);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  // Handler conectado para Reacciones ("Me gusta") en el Tablón
  const handleInteractToggle = async (publicacionId, hasUserInteracted) => {
    // Actualización optimista inmediata
    setPublicaciones((prevPubs) =>
      prevPubs.map((pub) => {
        if (pub.id !== publicacionId) return pub;
        const currentTotal = pub.totalInteracciones || 0;
        return {
          ...pub,
          hasUserInteracted: !hasUserInteracted,
          totalInteracciones: hasUserInteracted ? Math.max(0, currentTotal - 1) : currentTotal + 1,
        };
      })
    );

    try {
      if (hasUserInteracted) {
        await api.removeInteraction(publicacionId);
      } else {
        await api.addInteraction(publicacionId);
      }
    } catch (error) {
      console.error('Error al alternar reacción:', error);
      toast.error('No se pudo actualizar tu reacción');
      // Revertir en caso de error
      setPublicaciones((prevPubs) =>
        prevPubs.map((pub) => {
          if (pub.id !== publicacionId) return pub;
          const currentTotal = pub.totalInteracciones || 0;
          return {
            ...pub,
            hasUserInteracted: hasUserInteracted,
            totalInteracciones: hasUserInteracted ? currentTotal + 1 : Math.max(0, currentTotal - 1),
          };
        })
      );
    }
  };

  const handleViewResults = (cId, evaluationId) => {
    navigate(`/alumno/catedra/${cId}/evaluacion/${evaluationId}/results`);
  };

  const handleGoToTab = (tabName, idToHighlight = null) => {
    setActiveTab(tabName);
    if (idToHighlight) {
      navigate(location.pathname, { replace: true, state: { ...location.state, highlightId: idToHighlight, activeTab: tabName } });
    } else {
      navigate(location.pathname, { replace: true, state: { ...location.state, highlightId: undefined, activeTab: tabName } });
    }
  };

  useEffect(() => {
    if (!loading && catedra && location.state?.highlightId && location.state?.activeTab === 'tareas') {
      const taskToHighlightId = location.state.highlightId;
      const task = tareas.find(t => t.id === taskToHighlightId);
      if (task) {
        handleOpenSubmitModal(task);
      }
      navigate(location.pathname, { replace: true, state: { ...location.state, highlightId: undefined } });
    } else if (!loading && catedra && location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      navigate(location.pathname, { replace: true, state: { ...location.state, activeTab: undefined } });
    }
  }, [loading, catedra, tareas, location.state]);

  const fetchCatedraData = async () => {
    const parsedCatedraId = parseInt(catedraId);

    if (!catedraId || isNaN(parsedCatedraId) || parsedCatedraId <= 0) {
      toast.error('ID de cátedra inválido.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const studentCatedrasResponse = await api.getStudentCatedras();
      const studentCatedras = studentCatedrasResponse.data || [];

      const foundCatedraRaw = studentCatedras.find(c => {
        return c.catedraId === parsedCatedraId || c.id === parsedCatedraId || c.Catedra?.id === parsedCatedraId;
      });

      if (foundCatedraRaw) {
        setCatedra({
          ...foundCatedraRaw,
          id: foundCatedraRaw.catedraId || foundCatedraRaw.id
        });
      } else {
        setCatedra(null);
        toast.error('Cátedra no encontrada');
        setLoading(false);
        return;
      }

      // Fetch paralelo de publicaciones, tareas, evaluaciones, calificación final y datos del aula (Plan + Asistencia)
      const [pubResponse, tareasResponse, evalsResponse, finalGradeResponse, aulaResponse] = await Promise.all([
        api.getPublicaciones(parsedCatedraId).catch(() => ({ data: [] })),
        api.getAlumnoTareas().catch(() => ({ data: [] })),
        api.getMyEvaluations().catch(() => ({ data: [] })),
        api.getAlumnoFinalGrade(parsedCatedraId).catch(() => ({ data: null })),
        api.getAlumnoCatedraAula(parsedCatedraId).catch(() => ({ data: null }))
      ]);

      if (aulaResponse?.data) {
        setPlanesDeClase(aulaResponse.data.planesDeClase || []);
        if (aulaResponse.data.asistencia) {
          setAsistenciaData(aulaResponse.data.asistencia);
        }
        if (aulaResponse.data.catedra?.docente) {
          setDocenteInfo(aulaResponse.data.catedra.docente);
        }
      }

      const pubs = (pubResponse.data || []).sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );

      const tareasCatedra = (tareasResponse.data || []).filter(
        t => t.TareaMaestra?.Catedra?.id === parsedCatedraId
      );
      setTareas(tareasCatedra);

      const allEvals = evalsResponse.data || [];
      const evalsCatedra = allEvals.filter(
        e => e.Catedra?.id === parsedCatedraId
      );
      setEvaluaciones(evalsCatedra);

      if (finalGradeResponse?.data) {
        setFinalGradeResults(finalGradeResponse.data.resultados);
        setPorcentajeMinimoAprobacion(finalGradeResponse.data.config?.porcentajeMinimoAprobacion);
        setCalificacionFinalConfigId(finalGradeResponse.data.config?.id);
      } else {
        setFinalGradeResults([]);
        setPorcentajeMinimoAprobacion(null);
        setCalificacionFinalConfigId(null);
      }

      // Enriquecer publicaciones con estado de asignación
      const enrichedPubs = pubs.map(pub => {
        if (pub.tipo === 'EVALUACION' && pub.evaluacionAsignacionId) {
          const masterEvaluation = allEvals.find(e =>
            e.EvaluacionAsignacion?.some(ea => ea.id === pub.evaluacionAsignacionId)
          );
          if (masterEvaluation) {
            const assignment = masterEvaluation.EvaluacionAsignacion.find(ea => ea.id === pub.evaluacionAsignacionId);
            if (assignment) {
              return {
                ...pub,
                evaluacionAsignacionEstado: assignment.estado,
                evaluationMaestraId: masterEvaluation.id
              };
            }
          }
        } else if (pub.tipo === 'TAREA' && pub.tareaAsignacionId) {
          const assignedTask = tareasCatedra.find(t => t.id === pub.tareaAsignacionId);
          if (assignedTask) {
            return { ...pub, tareaAsignacionEstado: assignedTask.estado };
          }
        }
        return pub;
      });
      setPublicaciones(enrichedPubs);

      // Calcular stats
      const tareasPendientes = tareasCatedra.filter(
        t => t.estado === 'ASIGNADA' || (t.estado === 'VENCIDA' && (!t.submission_path || t.submission_path.length === 0))
      ).length;
      const tareasCompletadas = tareasCatedra.filter(
        t => t.estado === 'CALIFICADA'
      ).length;

      const getEstadoForStats = (evaluacion) => {
        if (evaluacion.EvaluacionAsignacion && evaluacion.EvaluacionAsignacion.length > 0) {
          return evaluacion.EvaluacionAsignacion[0].estado;
        }
        return evaluacion.estado || 'PENDIENTE';
      };

      const evaluacionesPendientes = evalsCatedra.filter(e => {
        const estado = getEstadoForStats(e);
        return estado === 'PENDIENTE' || estado === 'VENCIDA';
      }).length;

      const evaluacionesCompletadas = evalsCatedra.filter(e => {
        const estado = getEstadoForStats(e);
        return estado === 'REALIZADA' || estado === 'CALIFICADA';
      }).length;

      setStats({
        totalPublicaciones: pubs.length,
        totalTareas: tareasCatedra.length,
        tareasPendientes,
        tareasCompletadas,
        totalEvaluaciones: evalsCatedra.length,
        evaluacionesPendientes,
        evaluacionesCompletadas
      });
    } catch (err) {
      console.error('Error fetching cátedra data:', err);
      toast.error('Error al cargar los datos de la cátedra');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePublicacion = async (arg1, arg2) => {
    const payload = arg2 !== undefined ? arg2 : arg1;
    try {
      setPublicationLoading(true);
      await api.createPublicacion(parseInt(catedraId), payload);
      toast.success('Publicación creada exitosamente');
      setIsPublicacionModalOpen(false);
      fetchCatedraData();
    } catch (error) {
      console.error('Error al crear publicación:', error);
      toast.error('Error al crear la publicación');
    } finally {
      setPublicationLoading(false);
    }
  };

  const handleEditPublicacion = async (arg1, arg2) => {
    const payload = arg2 !== undefined ? arg2 : arg1;
    const pubId = arg2 !== undefined ? arg1 : editingPublicacion?.id;
    try {
      setPublicationLoading(true);
      await api.updatePublicacion(parseInt(catedraId), pubId, payload);
      toast.success('Publicación actualizada exitosamente');
      setIsPublicacionModalOpen(false);
      setEditingPublicacion(null);
      fetchCatedraData();
    } catch (error) {
      console.error('Error al actualizar publicación:', error);
      toast.error('Error al actualizar la publicación');
    } finally {
      setPublicationLoading(false);
    }
  };

  const handleDeletePublicacion = async (publicacionId) => {
    try {
      await api.deletePublicacion(parseInt(catedraId), publicacionId);
      toast.success('Publicación eliminada');
      fetchCatedraData();
    } catch (error) {
      console.error('Error al eliminar publicación:', error);
      toast.error('Error al eliminar la publicación');
    }
  };

  const handleAddComment = async (publicacionId, comentario) => {
    try {
      await api.createComentario(publicacionId, typeof comentario === 'string' ? { contenido: comentario } : comentario);
      fetchCatedraData();
    } catch (error) {
      console.error('Error al agregar comentario:', error);
      toast.error('Error al agregar el comentario');
    }
  };

  const handleDeleteComment = async (arg1, arg2) => {
    const publicacionId = arg2 !== undefined ? arg1 : null;
    const comentarioId = arg2 !== undefined ? arg2 : arg1;
    try {
      await api.deleteComentario(publicacionId, comentarioId);
      toast.success('Comentario eliminado');
      fetchCatedraData();
    } catch (error) {
      console.error('Error al eliminar comentario:', error);
      toast.error('Error al eliminar el comentario');
    }
  };

  const handleSubmitDelivery = async () => {
    if (selectedFiles.length === 0 || !taskToSubmit) {
      toast.error('Por favor selecciona al menos un archivo');
      return;
    }

    try {
      setLoading(true);
      await api.submitTaskDelivery(taskToSubmit.id, selectedFiles);
      toast.success(`${selectedFiles.length} archivo(s) entregado(s) exitosamente`);
      setIsSubmitModalOpen(false);
      setSelectedFiles([]);
      fetchCatedraData();
    } catch (error) {
      console.error('Error al subir entrega:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Error al subir la entrega');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING_REVIEW': 'bg-yellow-900/20 text-yellow-300 border-yellow-500/30',
      'PUBLISHED': 'bg-green-900/20 text-green-300 border-green-500/30',
      'REJECTED': 'bg-red-900/20 text-red-300 border-red-500/30',
      'NEEDS_IMPROVEMENT': 'bg-blue-900/20 text-blue-300 border-blue-500/30',
      'ASIGNADA': 'bg-blue-900/20 text-blue-300 border-blue-500/30',
      'ENTREGADA': 'bg-purple-900/20 text-purple-300 border-purple-500/30',
      'CALIFICADA': 'bg-emerald-900/20 text-emerald-300 border-emerald-500/30',
      'VENCIDA': 'bg-red-900/20 text-red-300 border-red-500/30'
    };
    return colors[status] || 'bg-slate-900/20 text-slate-300 border-slate-500/30';
  };

  const getTaskStatusDisplay = (task) => {
    if (task.estado === 'CALIFICADA') return 'Calificada';
    if (task.estado === 'ENTREGADA') return 'Entregada';
    if (task.estado === 'VENCIDA') return (task.submission_path && task.submission_path.length > 0) ? 'Vencida (Entregada)' : 'Vencida';
    if (task.estado === 'ASIGNADA') return 'Asignada';
    return task.estado || 'Desconocido';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const totalUnidadesCount = planesDeClase.reduce((acc, plan) => acc + (plan.UnidadPlan?.length || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-purple-600/30 rounded-full animate-spin border-t-purple-500"></div>
          </div>
          <p className="text-slate-300 text-lg font-medium">Cargando tu Aula Virtual...</p>
        </div>
      </div>
    );
  }

  if (!catedra) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Cátedra no encontrada</h2>
          <p className="text-slate-400 mb-4">No se pudo cargar la información de la cátedra.</p>
          <button
            onClick={() => navigate('/alumnos/dashboard')}
            className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header con info de la cátedra */}
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
          <button
            onClick={() => navigate('/alumnos/dashboard')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Dashboard
          </button>

          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-900/30">
              <BookOpen className="text-white" size={36} />
            </div>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                    {catedra.Catedra?.nombre || 'Cátedra'}
                  </h1>
                  <div className="flex flex-wrap gap-4 text-slate-300 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={16} className="text-emerald-400" />
                      <span>Año {catedra.Catedra?.anio || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User size={16} className="text-blue-400" />
                      <span>{catedra.Catedra?.institucion || 'Institución no especificada'}</span>
                    </div>
                    {docenteInfo && (
                      <div className="flex items-center gap-1.5">
                        <Sparkles size={16} className="text-purple-400" />
                        <span>Prof. {docenteInfo.nombre} {docenteInfo.apellido}</span>
                      </div>
                    )}
                    {catedra.Catedra?.dias && (
                      <div className="flex items-center gap-1.5">
                        <Clock size={16} className="text-amber-400" />
                        <span>{catedra.Catedra.dias} {catedra.Catedra.turno ? `(${catedra.Catedra.turno})` : ''}</span>
                      </div>
                    )}
                  </div>
                </div>
                {finalGradeResults && finalGradeResults.length > 0 && (
                  <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center bg-gradient-to-br from-fuchsia-600 to-purple-700 shadow-lg shadow-fuchsia-900/40 flex-shrink-0 text-white font-bold text-3xl p-2 border-2 border-white/20">
                    <p className="leading-none">{finalGradeResults[0].escalaNota}</p>
                    <p className="text-xs font-medium text-fuchsia-200 leading-none mt-1">
                      ({finalGradeResults[0].estadoEscala?.split(' ')[0]})
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
            <div onClick={() => setActiveTab('tablon')} className="cursor-pointer bg-purple-900/20 hover:bg-purple-900/30 border border-purple-500/30 rounded-xl p-4 transition-colors">
              <MessageSquare className="text-purple-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Publicaciones</p>
              <p className="text-white text-2xl font-bold">{stats.totalPublicaciones}</p>
            </div>
            <div onClick={() => setActiveTab('plan')} className="cursor-pointer bg-indigo-900/20 hover:bg-indigo-900/30 border border-indigo-500/30 rounded-xl p-4 transition-colors">
              <BookOpen className="text-indigo-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Unidades del Plan</p>
              <p className="text-white text-2xl font-bold">{totalUnidadesCount}</p>
            </div>
            <div onClick={() => setActiveTab('tareas')} className="cursor-pointer bg-yellow-900/20 hover:bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-4 transition-colors">
              <Clock className="text-yellow-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Tareas Pendientes</p>
              <p className="text-white text-2xl font-bold">{stats.tareasPendientes}</p>
            </div>
            <div onClick={() => setActiveTab('tareas')} className="cursor-pointer bg-green-900/20 hover:bg-green-900/30 border border-green-500/30 rounded-xl p-4 transition-colors">
              <CheckCircle className="text-green-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Tareas Calificadas</p>
              <p className="text-white text-2xl font-bold">{stats.tareasCompletadas}</p>
            </div>
            <div onClick={() => setActiveTab('evaluaciones')} className="cursor-pointer bg-orange-900/20 hover:bg-orange-900/30 border border-orange-500/30 rounded-xl p-4 transition-colors">
              <Brain className="text-orange-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Eval. Pendientes</p>
              <p className="text-white text-2xl font-bold">{stats.evaluacionesPendientes}</p>
            </div>
            <div onClick={() => setActiveTab('asistencia')} className="cursor-pointer bg-emerald-900/20 hover:bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-4 transition-colors">
              <CalendarCheck className="text-emerald-400 mb-2" size={22} />
              <p className="text-slate-400 text-xs">Mi Asistencia</p>
              <p className="text-white text-2xl font-bold">{asistenciaData.resumen.porcentajeAsistencia}%</p>
            </div>
          </div>
        </div>

        {/* Pestañas del Aula Virtual */}
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-800/30 border-b border-slate-700/50">
            <nav className="flex space-x-2 overflow-x-auto px-4 sm:space-x-6 sm:px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('tablon')}
                className={`${activeTab === 'tablon'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <MessageSquare size={18} />
                Tablón
                {stats.totalPublicaciones > 0 && (
                  <span className="ml-1 bg-emerald-600/20 text-emerald-300 px-2 py-0.5 rounded-full text-xs font-semibold border border-emerald-500/30">
                    {stats.totalPublicaciones}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('plan')}
                className={`${activeTab === 'plan'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <BookOpen size={18} />
                Plan de Estudios y Materiales
                {totalUnidadesCount > 0 && (
                  <span className="ml-1 bg-indigo-600/20 text-indigo-300 px-2 py-0.5 rounded-full text-xs font-semibold border border-indigo-500/30">
                    {totalUnidadesCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('tareas')}
                className={`${activeTab === 'tareas'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <FileText size={18} />
                Tareas
                {stats.totalTareas > 0 && (
                  <span className="ml-1 bg-yellow-600/20 text-yellow-300 px-2 py-0.5 rounded-full text-xs font-semibold border border-yellow-500/30">
                    {stats.totalTareas}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('evaluaciones')}
                className={`${activeTab === 'evaluaciones'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <Brain size={18} />
                Evaluaciones
                {stats.totalEvaluaciones > 0 && (
                  <span className="ml-1 bg-orange-600/20 text-orange-300 px-2 py-0.5 rounded-full text-xs font-semibold border border-orange-500/30">
                    {stats.totalEvaluaciones}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('asistencia')}
                className={`${activeTab === 'asistencia'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <CalendarCheck size={18} />
                Mi Asistencia
              </button>

              <button
                onClick={() => setActiveTab('calificacionFinal')}
                className={`${activeTab === 'calificacionFinal'
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
                  } group flex items-center gap-2 whitespace-nowrap py-4 px-3 border-b-2 font-medium text-base transition-all duration-300 rounded-t-lg`}
              >
                <Award size={18} />
                Calificación Final
              </button>
            </nav>
          </div>

          {/* Contenido de las pestañas */}
          <div className="p-6">
            {/* TAB: Tablón de Anuncios */}
            {activeTab === 'tablon' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white">Tablón de Anuncios</h3>
                    <p className="text-slate-400">Novedades, debates y anuncios de esta cátedra</p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingPublicacion(null);
                      setIsPublicacionModalOpen(true);
                    }}
                    className="group inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-xl transition-all duration-300 shadow-lg shadow-emerald-900/25"
                  >
                    <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                    <span>Nueva Publicación</span>
                  </button>
                </div>

                {(stats.tareasPendientes > 0 || stats.evaluacionesPendientes > 0) && (
                  <div className="bg-yellow-900/20 backdrop-blur-xl rounded-xl border border-yellow-500/30 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="text-yellow-400 w-6 h-6 flex-shrink-0" />
                      <p className="text-white text-base font-semibold">
                        Tienes actividades pendientes en esta cátedra:
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center sm:justify-end">
                      {stats.tareasPendientes > 0 && (
                        <button
                          onClick={() => handleGoToTab('tareas')}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg flex items-center gap-2 transition-colors font-medium text-sm"
                        >
                          <FileText size={18} />
                          {stats.tareasPendientes} Tarea{stats.tareasPendientes > 1 ? 's' : ''}
                        </button>
                      )}
                      {stats.evaluacionesPendientes > 0 && (
                        <button
                          onClick={() => handleGoToTab('evaluaciones')}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-colors font-medium text-sm"
                        >
                          <Brain size={18} />
                          {stats.evaluacionesPendientes} Evaluación{stats.evaluacionesPendientes > 1 ? 'es' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {publicaciones.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-4 bg-slate-800/50 rounded-full flex items-center justify-center">
                      <MessageSquare className="text-slate-500" size={32} />
                    </div>
                    <p className="text-slate-400 text-lg font-medium">No hay publicaciones aún</p>
                    <p className="text-slate-500 text-sm mt-1">Sé el primero en compartir una consulta o novedad</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {publicaciones.map(pub => (
                      <PublicacionCard
                        key={pub.id}
                        publicacion={pub}
                        onAddComment={handleAddComment}
                        onDeletePublication={handleDeletePublicacion}
                        onDeleteComment={handleDeleteComment}
                        onEditPublication={() => {
                          setEditingPublicacion(pub);
                          setIsPublicacionModalOpen(true);
                        }}
                        onInteractToggle={handleInteractToggle}
                        userType="alumno"
                        userId={currentAlumnoId}
                        onGoToTaskTab={handleGoToTaskTab}
                        onGoToTab={handleGoToTab}
                        getStatusColor={getStatusColor}
                        getTaskStatusDisplay={getTaskStatusDisplay}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB: Plan de Estudios y Materiales */}
            {activeTab === 'plan' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Plan de Estudios y Materiales de Clase</h3>
                  <p className="text-slate-400">
                    Explora los contenidos, objetivos, estrategias y recursos multimedia de cada unidad
                  </p>
                </div>

                {planesDeClase.length === 0 ? (
                  <div className="text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/50">
                    <BookOpen className="w-16 h-16 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-300 text-lg font-medium">El docente aún no ha publicado el plan de estudios</p>
                    <p className="text-slate-500 text-sm mt-1">Aquí aparecerán las unidades y materiales de apoyo de la cátedra.</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {planesDeClase.map((plan) => (
                      <div key={plan.id} className="bg-slate-800/40 rounded-2xl border border-slate-700/60 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 px-6 py-4 border-b border-slate-700/60 flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <BookOpen className="text-indigo-400" size={24} />
                            <div>
                              <h4 className="text-xl font-bold text-white">{plan.titulo}</h4>
                              <p className="text-xs text-slate-400">
                                Organización: {plan.tipoOrganizacion === 'MES' ? 'Por Mes' : 'Por Módulo / Unidad'}
                              </p>
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                            {plan.UnidadPlan?.length || 0} unidad(es)
                          </span>
                        </div>

                        <div className="p-6 space-y-6">
                          {(!plan.UnidadPlan || plan.UnidadPlan.length === 0) ? (
                            <p className="text-slate-400 text-sm">No hay unidades cargadas en este plan.</p>
                          ) : (
                            plan.UnidadPlan.map((unidad, idx) => (
                              <div
                                key={unidad.id}
                                className="bg-slate-900/60 rounded-xl border border-slate-700/50 p-5 space-y-4 hover:border-indigo-500/40 transition-colors"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-3">
                                  <div className="flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-indigo-600/30 border border-indigo-500/40 text-indigo-300 font-bold flex items-center justify-center text-sm">
                                      {idx + 1}
                                    </span>
                                    <h5 className="text-lg font-bold text-white">{unidad.periodo}</h5>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
                                      {unidad.horasTeoricas} hs teóricas
                                    </span>
                                    <span className="px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">
                                      {unidad.horasPracticas} hs prácticas
                                    </span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2">
                                      Contenidos
                                    </p>
                                    <p className="text-slate-200 whitespace-pre-line leading-relaxed">
                                      {unidad.contenido || 'Sin especificar'}
                                    </p>
                                  </div>

                                  <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                                      Capacidades y Objetivos
                                    </p>
                                    <p className="text-slate-200 whitespace-pre-line leading-relaxed">
                                      {unidad.capacidades || 'Sin especificar'}
                                    </p>
                                  </div>

                                  {unidad.estrategiasMetodologicas && (
                                    <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40">
                                      <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-2">
                                        Estrategias Metodológicas
                                      </p>
                                      <p className="text-slate-300 whitespace-pre-line leading-relaxed">
                                        {unidad.estrategiasMetodologicas}
                                      </p>
                                    </div>
                                  )}

                                  {unidad.mediosVerificacionEvaluacion && (
                                    <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40">
                                      <p className="text-xs font-semibold uppercase tracking-wider text-purple-400 mb-2">
                                        Medios de Verificación / Evaluación
                                      </p>
                                      <p className="text-slate-300 whitespace-pre-line leading-relaxed">
                                        {unidad.mediosVerificacionEvaluacion}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Recursos Multimedia de la Unidad */}
                                {unidad.recursos && unidad.recursos.length > 0 && (
                                  <div className="pt-3 border-t border-slate-800">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-pink-400 mb-3 flex items-center gap-1.5">
                                      <Paperclip size={14} />
                                      Materiales y Recursos de la Unidad ({unidad.recursos.length})
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {unidad.recursos.map((recurso, rIdx) => {
                                        const recObj = typeof recurso === 'string' ? { type: 'link', value: recurso } : recurso;
                                        const ytId = recObj.type === 'youtube' ? getYouTubeVideoId(recObj.value) : null;

                                        if (ytId) {
                                          return (
                                            <div key={rIdx} className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/60">
                                              <div className="aspect-video w-full rounded-lg overflow-hidden mb-2">
                                                <iframe
                                                  width="100%"
                                                  height="100%"
                                                  src={`https://www.youtube.com/embed/${ytId}`}
                                                  frameBorder="0"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen
                                                  title={`Recurso YouTube ${rIdx + 1}`}
                                                />
                                              </div>
                                              <a
                                                href={recObj.value}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 font-medium"
                                              >
                                                <Youtube size={14} /> Abrir en YouTube
                                              </a>
                                            </div>
                                          );
                                        }

                                        const rawVal = recObj.value || '';
                                        const fullUrl = rawVal.startsWith('http')
                                          ? rawVal
                                          : `${STATIC_ASSET_BASE_URL}${rawVal.startsWith('/') ? '' : '/'}${rawVal}`;

                                        return (
                                          <a
                                            key={rIdx}
                                            href={fullUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-purple-500/40 transition-all group"
                                          >
                                            <div className="flex items-center gap-3 min-w-0">
                                              {recObj.type === 'file' ? (
                                                <FileText size={20} className="text-purple-400 flex-shrink-0" />
                                              ) : (
                                                <Globe size={20} className="text-blue-400 flex-shrink-0" />
                                              )}
                                              <span className="text-sm text-slate-200 group-hover:text-white truncate font-medium">
                                                {rawVal.split('/').pop() || rawVal}
                                              </span>
                                            </div>
                                            <ExternalLink size={16} className="text-slate-400 group-hover:text-purple-400 flex-shrink-0" />
                                          </a>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB: Tareas */}
            {activeTab === 'tareas' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Tareas de la Cátedra</h3>
                  <p className="text-slate-400">Gestiona tus entregas, descarga recursos del docente y revisa tus devoluciones</p>
                </div>

                {tareas.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-4 bg-slate-800/50 rounded-full flex items-center justify-center">
                      <FileText className="text-slate-500" size={32} />
                    </div>
                    <p className="text-slate-400 text-lg font-medium">No hay tareas asignadas en esta cátedra</p>
                  </div>
                ) : (
                  <>
                    <TaskTable
                      title="Pendientes de Entrega"
                      tasks={tareas.filter(t => t.estado === 'ASIGNADA' || (t.estado === 'VENCIDA' && (!t.submission_path || t.submission_path.length === 0)))}
                      getStatusColor={getStatusColor}
                      getTaskStatusDisplay={getTaskStatusDisplay}
                      handleOpenSubmitModal={handleOpenSubmitModal}
                      onViewStudentTaskDetail={handleViewStudentTaskDetail}
                      showActions={true}
                    />
                    <TaskTable
                      title="Entregadas (En Revisión del Docente)"
                      tasks={tareas.filter(t => t.estado === 'ENTREGADA' || (t.estado === 'VENCIDA' && t.submission_path && t.submission_path.length > 0))}
                      getStatusColor={getStatusColor}
                      getTaskStatusDisplay={getTaskStatusDisplay}
                      onViewStudentTaskDetail={handleViewStudentTaskDetail}
                      showActions={false}
                    />
                    <TaskTable
                      title="Calificadas y con Devolución"
                      tasks={tareas.filter(t => t.estado === 'CALIFICADA')}
                      getStatusColor={getStatusColor}
                      getTaskStatusDisplay={getTaskStatusDisplay}
                      onViewStudentTaskDetail={handleViewStudentTaskDetail}
                      showActions={false}
                      showPoints={true}
                    />
                  </>
                )}
              </div>
            )}

            {/* TAB: Evaluaciones */}
            {activeTab === 'evaluaciones' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Evaluaciones de la Cátedra</h3>
                  <p className="text-slate-400">Completa tus evaluaciones y revisa tus resultados detallados</p>
                </div>

                {evaluaciones.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-4 bg-slate-800/50 rounded-full flex items-center justify-center">
                      <Brain className="text-slate-500" size={32} />
                    </div>
                    <p className="text-slate-400 text-lg font-medium">No hay evaluaciones en esta cátedra</p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const getEstado = (evaluacion) => {
                        if (evaluacion.EvaluacionAsignacion && evaluacion.EvaluacionAsignacion.length > 0) {
                          return evaluacion.EvaluacionAsignacion[0].estado;
                        }
                        return evaluacion.estado || 'PENDIENTE';
                      };

                      const pendientes = evaluaciones.filter(e => {
                        const estado = getEstado(e);
                        return estado === 'PENDIENTE' || estado === 'VENCIDA';
                      });

                      const completadas = evaluaciones.filter(e => {
                        const estado = getEstado(e);
                        return estado === 'REALIZADA' || estado === 'CALIFICADA';
                      });

                      return (
                        <>
                          <EvaluationTable
                            title="Pendientes"
                            evaluations={pendientes}
                            getStatusColor={getStatusColor}
                            showActions={true}
                          />
                          <EvaluationTable
                            title="Completadas"
                            evaluations={completadas}
                            getStatusColor={getStatusColor}
                            showResultsButton={true}
                            onViewResults={handleViewResults}
                          />
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* TAB: Mi Asistencia */}
            {activeTab === 'asistencia' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Mi Registro de Asistencia</h3>
                  <p className="text-slate-400">Seguimiento detallado de tus asistencias a clase en esta cátedra</p>
                </div>

                {/* Resumen de Asistencia */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 rounded-xl p-5 border border-emerald-500/30">
                    <p className="text-xs text-slate-400 uppercase font-semibold">Porcentaje de Asistencia</p>
                    <p className="text-3xl font-extrabold text-emerald-400 mt-1">
                      {asistenciaData.resumen.porcentajeAsistencia}%
                    </p>
                    <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden mt-3">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full"
                        style={{ width: `${Math.min(100, asistenciaData.resumen.porcentajeAsistencia)}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-xl p-5 border border-green-500/30 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-semibold">Clases Presente</p>
                      <p className="text-3xl font-bold text-white mt-1">{asistenciaData.resumen.diasPresente}</p>
                    </div>
                    <CheckCircle className="text-green-400" size={32} />
                  </div>

                  <div className="bg-slate-800/50 rounded-xl p-5 border border-red-500/30 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-semibold">Ausencias</p>
                      <p className="text-3xl font-bold text-white mt-1">{asistenciaData.resumen.diasAusente}</p>
                    </div>
                    <XCircle className="text-red-400" size={32} />
                  </div>

                  <div className="bg-slate-800/50 rounded-xl p-5 border border-blue-500/30 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-semibold">Feriados / Asuetos / Lluvia</p>
                      <p className="text-3xl font-bold text-white mt-1">{asistenciaData.resumen.diasEspeciales}</p>
                    </div>
                    <Calendar className="text-blue-400" size={32} />
                  </div>
                </div>

                {/* Listado Cronológico de Clases */}
                {asistenciaData.registros.length === 0 ? (
                  <div className="text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/50">
                    <CalendarCheck className="w-16 h-16 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-300 text-lg font-medium">Aún no se han registrado días de clase</p>
                  </div>
                ) : (
                  <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-700/60">
                        <thead className="bg-slate-800/90">
                          <tr>
                            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Fecha</th>
                            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Día</th>
                            <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Tipo de Clase</th>
                            <th className="px-6 py-3.5 text-center text-xs font-semibold text-slate-300 uppercase">Mi Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {asistenciaData.registros.map((reg) => {
                            const badgeStyle = {
                              PRESENTE: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
                              AUSENTE: 'bg-red-500/20 text-red-300 border-red-500/30',
                              FERIADO: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
                              ASUETO: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
                              LLUVIA: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
                              PENDIENTE: 'bg-slate-700/50 text-slate-300 border-slate-600/50',
                            }[reg.estado] || 'bg-slate-700/50 text-slate-300 border-slate-600/50';

                            return (
                              <tr key={reg.id} className="hover:bg-slate-700/20 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                                  {new Date(reg.fecha).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300 capitalize">
                                  {reg.dia_semana}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                                  {reg.tipoDia}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badgeStyle}`}>
                                    {reg.estado === 'PENDIENTE' ? 'Sin llamar lista aún' : reg.estado}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB: Calificación Final */}
            {activeTab === 'calificacionFinal' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Calificación Final</h3>
                  <p className="text-slate-400">Consulta tu calificación final y ponderación en esta cátedra</p>
                </div>
                <AlumnoFinalGradeDisplay
                  backendResults={finalGradeResults}
                  porcentajeMinimoAprobacion={porcentajeMinimoAprobacion}
                  selectedAlumnoId={currentAlumnoId}
                  configId={calificacionFinalConfigId}
                  catedraId={parseInt(catedraId)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar Publicación */}
      <Modal
        isOpen={isPublicacionModalOpen}
        onClose={() => {
          setIsPublicacionModalOpen(false);
          setEditingPublicacion(null);
        }}
        title={editingPublicacion ? 'Editar Publicación' : 'Nueva Publicación'}
        showSubmitButton={false}
        showCancelButton={false}
      >
        <PublicacionForm
          catedraId={parseInt(catedraId)}
          onSubmit={editingPublicacion ? handleEditPublicacion : handleCreatePublicacion}
          initialData={editingPublicacion || {}}
          isEditMode={!!editingPublicacion}
          loading={publicationLoading}
          onCancel={() => {
            setIsPublicacionModalOpen(false);
            setEditingPublicacion(null);
          }}
          availableCatedras={[catedra]}
          userRole={userRole}
        />
      </Modal>

      {/* Modal de Detalle de Tarea y Devolución del Docente */}
      {selectedTaskForDetail && (
        <Modal
          isOpen={isTaskDetailModalOpen}
          onClose={() => {
            setIsTaskDetailModalOpen(false);
            setSelectedTaskForDetail(null);
          }}
          title={`Detalle de Tarea: ${selectedTaskForDetail.TareaMaestra?.titulo || ''}`}
          showSubmitButton={false}
          cancelText="Cerrar"
        >
          <div className="p-5 space-y-6 text-slate-200">
            {/* Resumen superior */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-slate-800/60 rounded-xl p-3.5 border border-slate-700/60">
                <span className="text-xs text-slate-400 block">Estado</span>
                <span className={`mt-1 inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(selectedTaskForDetail.estado)}`}>
                  {getTaskStatusDisplay(selectedTaskForDetail)}
                </span>
              </div>
              <div className="bg-slate-800/60 rounded-xl p-3.5 border border-slate-700/60">
                <span className="text-xs text-slate-400 block">Fecha de Vencimiento</span>
                <span className="text-sm font-bold text-white mt-1 block">
                  {selectedTaskForDetail.TareaMaestra?.fecha_entrega
                    ? new Date(selectedTaskForDetail.TareaMaestra.fecha_entrega).toLocaleDateString()
                    : 'Sin fecha límite'}
                </span>
              </div>
              <div className="bg-slate-800/60 rounded-xl p-3.5 border border-slate-700/60">
                <span className="text-xs text-slate-400 block">Puntaje</span>
                <span className="text-sm font-bold text-emerald-400 mt-1 block">
                  {selectedTaskForDetail.puntos_obtenidos !== null && selectedTaskForDetail.puntos_obtenidos !== undefined
                    ? `${selectedTaskForDetail.puntos_obtenidos} / ${selectedTaskForDetail.TareaMaestra?.puntos_posibles} pts`
                    : `${selectedTaskForDetail.TareaMaestra?.puntos_posibles || 0} pts posibles`}
                </span>
              </div>
            </div>

            {/* Devolución / Feedback del Docente (si existe) */}
            {selectedTaskForDetail.comentario_docente && (
              <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-xl p-4">
                <h4 className="text-sm font-bold text-emerald-300 flex items-center gap-2 mb-1.5">
                  <MessageSquare size={16} />
                  Devolución del Profesor
                </h4>
                <p className="text-slate-200 text-sm whitespace-pre-line">
                  {selectedTaskForDetail.comentario_docente}
                </p>
              </div>
            )}

            {/* Consigna / Descripción */}
            <div>
              <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2">
                Consigna / Descripción
              </h4>
              <div
                className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/50 prose prose-sm prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedTaskForDetail.TareaMaestra?.descripcion || '<p>Sin descripción adicional.</p>' }}
              />
            </div>

            {/* Recursos Adjuntos por el Docente */}
            {selectedTaskForDetail.TareaMaestra?.recursos && selectedTaskForDetail.TareaMaestra.recursos.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Paperclip size={15} />
                  Material de Apoyo Adjunto por el Docente
                </h4>
                <div className="grid gap-2">
                  {selectedTaskForDetail.TareaMaestra.recursos.map((recurso, idx) => {
                    const fileName = recurso.split('/').pop();
                    const fullUrl = recurso.startsWith('http')
                      ? recurso
                      : `${STATIC_ASSET_BASE_URL}${recurso.startsWith('/') ? '' : '/'}${recurso}`;
                    return (
                      <a
                        key={idx}
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                      >
                        <span className="flex items-center gap-2 truncate">
                          <FileText size={16} />
                          {fileName}
                        </span>
                        <ExternalLink size={15} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Archivos Entregados por el Alumno */}
            {selectedTaskForDetail.submission_path && selectedTaskForDetail.submission_path.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <CheckCircle size={15} />
                  Mis Archivos Entregados
                  {selectedTaskForDetail.submission_date && (
                    <span className="text-xs text-slate-400 font-normal ml-1">
                      ({new Date(selectedTaskForDetail.submission_date).toLocaleString()})
                    </span>
                  )}
                </h4>
                <div className="grid gap-2">
                  {(Array.isArray(selectedTaskForDetail.submission_path)
                    ? selectedTaskForDetail.submission_path
                    : [selectedTaskForDetail.submission_path]
                  ).map((path, idx) => {
                    const fileName = path.split('/').pop();
                    const fullUrl = path.startsWith('http')
                      ? path
                      : `${STATIC_ASSET_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
                    return (
                      <a
                        key={idx}
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-xl bg-purple-950/30 hover:bg-purple-950/50 border border-purple-500/30 text-purple-300 hover:text-purple-200 text-sm font-medium transition-colors"
                      >
                        <span className="flex items-center gap-2 truncate">
                          <FileText size={16} />
                          {fileName}
                        </span>
                        <ExternalLink size={15} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Botón para ir a subir o actualizar entrega si no está calificada */}
            {selectedTaskForDetail.estado !== 'CALIFICADA' && (
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const task = selectedTaskForDetail;
                    setIsTaskDetailModalOpen(false);
                    handleOpenSubmitModal(task);
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-semibold shadow-lg transition-all"
                >
                  <Upload size={16} />
                  {selectedTaskForDetail.submission_path?.length > 0 ? 'Actualizar Archivos de Entrega' : 'Subir Entrega Ahora'}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal de Subida de Entrega con Drag & Drop */}
      <Modal
        isOpen={isSubmitModalOpen}
        onClose={() => {
          setIsSubmitModalOpen(false);
          setSelectedFiles([]);
          setIsDragging(false);
        }}
        title="Subir Entrega de Tarea"
        onSubmit={handleSubmitDelivery}
        submitText="Enviar Entrega"
        submitDisabled={selectedFiles.length === 0 || loading}
      >
        <div className="p-6 space-y-6">
          {/* Información de la tarea */}
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-5 border border-purple-500/30 space-y-3">
            <h3 className="text-xl font-bold text-purple-300 flex items-center gap-2">
              <FileText size={24} />
              {taskToSubmit?.TareaMaestra?.titulo}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <BookOpen size={16} className="text-emerald-400" />
                <span><strong>Cátedra:</strong> {taskToSubmit?.TareaMaestra?.Catedra?.nombre || 'N/A'}</span>
              </div>

              {taskToSubmit?.TareaMaestra?.UnidadPlan?.PlanDeClases?.titulo && (
                <div className="flex items-center gap-2 text-slate-300">
                  <BookOpen size={16} className="text-blue-400" />
                  <span><strong>Plan:</strong> {taskToSubmit.TareaMaestra.UnidadPlan.PlanDeClases.titulo}</span>
                </div>
              )}

              {taskToSubmit?.TareaMaestra?.UnidadPlan?.periodo && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Target size={16} className="text-purple-400" />
                  <span><strong>Unidad:</strong> {taskToSubmit.TareaMaestra.UnidadPlan.periodo}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-slate-300">
                <Calendar size={16} className="text-yellow-400" />
                <span>
                  <strong>Vence:</strong>{' '}
                  {taskToSubmit?.TareaMaestra?.fecha_entrega
                    ? new Date(taskToSubmit.TareaMaestra.fecha_entrega).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>

              <div className="flex items-center gap-2 text-slate-300">
                <Award size={16} className="text-green-400" />
                <span><strong>Puntos:</strong> {taskToSubmit?.TareaMaestra?.puntos_posibles || 0}</span>
              </div>
            </div>

            {taskToSubmit?.TareaMaestra?.descripcion && (
              <div className="pt-3 border-t border-slate-700/50">
                <p className="text-xs text-slate-400 mb-1 font-semibold">Consigna:</p>
                <div
                  className="text-sm text-slate-300 max-h-32 overflow-y-auto prose prose-sm prose-invert"
                  dangerouslySetInnerHTML={{ __html: taskToSubmit.TareaMaestra.descripcion }}
                />
              </div>
            )}
          </div>

          {/* Zona Drag & Drop Interactiva */}
          <div className="space-y-4">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all duration-200 ${
                isDragging
                  ? 'border-emerald-400 bg-emerald-500/15 scale-[1.01]'
                  : 'border-slate-600 hover:border-purple-400 bg-slate-800/40 hover:bg-slate-800/70'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mx-auto mb-3 text-purple-300">
                <Upload size={28} />
              </div>
              <p className="text-white font-semibold text-base">
                Arrastra y suelta tus archivos aquí, o <span className="text-purple-400 underline">haz clic para explorar</span>
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Soporta PDF, imágenes (JPG, PNG), documentos (DOCX, PPTX, XLSX) y comprimidos (máx. 20MB por archivo)
              </p>
            </div>

            {/* Lista de archivos seleccionados */}
            {selectedFiles.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/60 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-300 font-semibold">
                    {selectedFiles.length} archivo(s) listo(s) para entregar:
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedFiles([])}
                    className="text-xs text-red-400 hover:text-red-300 font-medium"
                  >
                    Vaciar lista
                  </button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="p-3 bg-emerald-950/30 border border-emerald-500/30 rounded-lg flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <CheckCircle size={18} className="text-emerald-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-emerald-200 font-medium text-sm truncate">{file.name}</p>
                          <p className="text-emerald-400/70 text-xs">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="p-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                        title="Quitar archivo"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-slate-700/50 flex justify-between text-xs text-slate-400">
                  <span>Tamaño total seleccionado:</span>
                  <strong className="text-slate-200">
                    {formatFileSize(selectedFiles.reduce((acc, file) => acc + file.size, 0))}
                  </strong>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AlumnoCatedraDetailPage;
